import { AlertType, VesselAlert } from '../../../src/models/vessel-alerts-model'
import { formatToTitleCase } from '../../../src/utils/helpers'
import { openLog, stateReason, reviewChange, reviewLoadstarChanges, showPlan, viewAdvice, viewReport, confirmationToOfflineMode, viewSoundingOnChart } from './alert-actions'
import { getCounterProposalAlertTitle } from './alert-helpers'

export type AlertStatus = 'info' | 'warning' | 'error' | 'success'

/**
 * Calculate priority score for an alert
 * Lower score = higher priority (appears on top)
 *
 * Priority order:
 * 0-1: error (0: non-closable, 1: closable)
 * 2-3: warning (2: non-closable, 3: closable)
 * 4-5: info (4: non-closable, 5: closable)
 * 6-7: success (6: non-closable, 7: closable)
 */
export function getPriorityOrder({
    status,
    closable,
}: {
    status?: AlertStatus
    closable?: boolean
}): number {
    if (!status) return 999

    const statusPriority: Record<AlertStatus, number> = {
        error: 0,
        warning: 2,
        info: 4,
        success: 6,
    }

    let priority = statusPriority[status] ?? 8

    if (closable) {
        priority += 1
    }

    return priority
}

export interface AlertConfig {
    appearance: AlertStatus
    closable: boolean
    title?: string | null
    showTime?: boolean
    action?: {
        text: string
        onClick: () => void
    }
    actions?: {
        text: string
        onClick: () => void
    }[]
    subText?: string
}

// depth_source.type is the S-57 ENC object class behind a leg's depth figure (SOUNDG/DEPARE/DRGARE),
// translated to its official S-57 name for use as the per-entry label (e.g. "Dredged Area 1 — ...").
const DEPTH_SOURCE_TYPE_LABELS: { [type: string]: string } = {
    sounding: 'Sounding',
    depare: 'Depth Area',
    dredged_area: 'Dredged Area',
}

function getDepthSourceTypeLabel(sourceType: string): string {
    return DEPTH_SOURCE_TYPE_LABELS[sourceType] ?? formatToTitleCase(sourceType.replace(/_/g, ' '))
}

export function getAlertConfig(alert: VesselAlert): AlertConfig | null {
    const formattedTitle = alert.title ? formatToTitleCase(alert.title) : null

    const configs: Record<string, AlertConfig> = {
        [AlertType.SubmittedAlarm]: {
            appearance: 'info',
            closable: true,
            title: formattedTitle,
            showTime: true,
            action: {
                text: 'See details in alarm log settings',
                onClick: openLog,
            },
        },
        [AlertType.AlarmReminder]: {
            appearance: 'error',
            closable: false,
            title: formattedTitle,
            showTime: true,
            action: {
                text: 'Please state reason',
                onClick: () => stateReason(alert.id),
            },
        },
        [AlertType.NoPlanEtd]: {
            title: formattedTitle,
            appearance: 'success',
            closable: true,
        },
        [AlertType.SuccessAlert]: {
            title: formattedTitle,
            appearance: 'success',
            closable: true,
        },
        [AlertType.NoPlanPreEtd]: {
            appearance: 'info',
            closable: true,
            title: 'You have not submitted a voyage plan via StarConnect',
            showTime: true,
        },

        [AlertType.BestPracticeRouteUpdated]: {
            title: formattedTitle,
            appearance: 'info',
            closable: true,
        },

        [AlertType.OutdatedENC]: {
            title: 'Update the ENC charts',
            appearance: 'info',
            closable: true,
            action: {
                text: 'How to export charts from Wendis Viewer',
                onClick: () => window.open('assets/documents/wendis_viewer_export.pdf', '_blank'),
            },
        },

        [AlertType.EtaChangeRemindLater]: {
            title: 'Action Required',
            appearance: 'warning',
            closable: true,
            showTime: true,
            action: {
                text: 'Review Change',
                onClick: () => reviewChange(),
            },
        },
        [AlertType.LoadstarUpdated]: {
            title: 'Stowage Plan Updated',
            appearance: 'info',
            closable: false,
            showTime: true,
            action: {
                text: 'Review changes',
                onClick: () => reviewLoadstarChanges(alert.id),
            },
        },
        // For WEATHER ROUTING:
        [AlertType.WNIReports]: {
            appearance: 'info',
            closable: false,
            title: 'WNI Report',
            showTime: true,
            action: {
                text: 'View Report',
                onClick: () =>
                    viewReport({
                        id: alert.id,
                        reportType: alert.reportType,
                        scheduleId: alert.scheduleId,
                        reportId: alert.reportId,
                    }),
            },
        },
        [AlertType.WeatherRoutingAdviceAvailable]: {
            title: formattedTitle,
            appearance: 'warning',
            closable: false,
            action: {
                text: 'View Advice',
                onClick: () => viewAdvice(alert.id),
            },
        },
        [AlertType.WeatherRoutingAdviceActionNeeded]: {
            appearance: 'warning',
            title: formattedTitle,
            closable: false,
            action: {
                text: 'View Advice',
                onClick: () =>
                    showPlan({
                        id: alert.id,
                        scheduleId: alert.scheduleId,
                    }),
            },
        },
        [AlertType.CounterProposalNotification]: {
            appearance: 'error',
            closable: false,
            title: getCounterProposalAlertTitle(),
            action: {
                text: 'Create/Upload Route',
                onClick: () =>
                    showPlan({
                        id: alert.id,
                        scheduleId: alert.scheduleId,
                    }),
            },
        },
        [AlertType.basicPlanAvailable]: {
            appearance: 'info',
            closable: true,
            title: 'Continue passage plan via Basic 2.0',
            subText: 'Please continue passage plan via Basic 2.0 until your next arrival port.'
        },
        [AlertType.slowNetworkDetected]: {
            appearance: 'warning',
            closable: true,
            title: 'Experiencing slow network issue',
            subText: 'Connectivity issue may affect passage planning temporarily.',
            action: {
                text: 'Switch to offline mode',
                onClick: () =>
                    confirmationToOfflineMode(),
            },
        },
        [AlertType.OpenOceanRouteReadinessWarning]: {
            appearance: 'warning',
            closable: true,
            title: 'Open Ocean Route Readiness Warning',
            subText: 'Functionality for calculating open ocean routes appears to be unhealthy.',
        },
        [AlertType.ShallowSoundingsWarning]: {
            appearance: 'warning',
            closable: false,
            title: 'Shallow Water on Selected Route',
            actions: (alert.shallowSoundings ?? []).map((sounding, index) => {
                const label = sounding.sourceType ? getDepthSourceTypeLabel(sounding.sourceType) : 'Sounding'
                return {
                    text: `${label} ${index + 1} — ${sounding.depth.toFixed(2)}m (CATZOC ${sounding.catzoc})`
                        + (sounding.ukc !== undefined ? ` · UKC ${sounding.ukc.toFixed(1)}m` : ''),
                    onClick: () => viewSoundingOnChart(sounding.coords),
                }
            }),
        },
        [AlertType.StarRouteNegativeUkcWarning]: {
            appearance: 'warning',
            closable: true,
            title: 'Warning: All StarRoutes fail UKC check',
        },
    }

    // returning a default config as fallback
    return alert.type
        ? configs[alert.type]
        : {
              title: formattedTitle,
              appearance: 'error',
              closable: true,
          }
}
